// Intake DTOs (S7) — BUILD_SPEC.md §5.4, §7, §8.1, §9.3. Owned by G14 (plan.md) —
// this is the ONLY core file G14 fills; the barrel (schemas/index.ts) already
// re-exports it.
//
// `TICKET_STATUSES`/`GOODS_RECEIPT_STATUSES` are IMPORTED from `../enums.js`
// (already ratified there ahead of this goal) rather than re-declared — mirrors
// schemas/sales.ts's precedent for `DISPATCH_STATUSES`/`VAT_TREATMENTS` (a second
// local `export const` of the same name would collide at `packages/core/src/
// index.ts`'s barrel, which re-exports both `./enums.js` and `./schemas/index.js`).
// `LANDED_COST_DOC_STATUSES` has no `@zebra/core/enums.ts` counterpart (no goal has
// been scoped to add one — same situation schemas/stores.ts flags for its own
// STORES_ISSUE_STATUSES/TRANSFER_STATUSES) — declared and exported locally here,
// which is collision-free since nothing else defines that name.
import { z } from 'zod';
import { moisturePctSchema, moneyCentsSchema, quantitySchema, ulidSchema } from './common.js';
import { GOODS_RECEIPT_STATUSES, TICKET_STATUSES } from '../enums.js';
export const TICKET_DIRECTIONS = ['IN', 'OUT'];
export const MC_SOURCES = ['LAB', 'METER', 'VISUAL', 'ASSUMED'];
export const LANDED_COST_DOC_STATUSES = ['DRAFT', 'POSTED', 'REVERSED'];
// ---------------------------------------------------------------------------
// Shared building blocks
// ---------------------------------------------------------------------------
const listQueryBaseSchema = z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().positive().max(200).default(50),
});
/** Mass (kg), max 3dp, non-negative — gross/tare weighbridge readings (a tare can
 * legitimately be zero for an unusual rig, so this allows it; a genuinely negative
 * reading never makes physical sense). */
const nonNegativeKgSchema = quantitySchema.refine((v) => Number(v) >= 0, 'must not be negative');
/** Mass (kg), max 3dp, strictly positive — a goods-receipt line's received quantity. */
const positiveKgSchema = quantitySchema.refine((v) => Number(v) > 0, 'must be greater than zero');
/** Count (EACH base UOM), strictly positive WHOLE units — a goods-receipt line
 * received against an EACH-priced (count) purchase order line (§-EACH). Same
 * decimal-string idiom as positiveKgSchema/quantitySchema, but fractional
 * counts are rejected (fix round 2026-07-07): "receive 2.5 boxes" would invent
 * sub-cent money at the `eachPayableCents` half-up rounding boundary. A whole
 * value written with decimals ('2.000') still passes — matching the DB CHECK
 * `qty = trunc(qty)` (packages/db/src/schema/intake.ts,
 * `goods_receipt_lines_qty_integer`) and schemas/purchasing.ts's qtyEachSchema. */
const positiveEachQtySchema = quantitySchema
    .refine((v) => Number(v) > 0, 'must be greater than zero')
    .refine((v) => Number.isInteger(Number(v)), 'must be a whole number of units');
/** Mandatory reason for a MOISTURE_BAND ADMIN out-of-band override (spec §7
 * amendment R8, ratified 2026-07-07). Trimmed, min length 3 (defense-in-depth
 * above the DB CHECK's bare `length(btrim(...)) > 0` — a 1-2 character reason
 * carries no audit value), max length 500 (audit fix round 2026-07-07: an
 * unbounded text field on a money-path input is an abuse surface; the DB column
 * stays plain `text` — THIS zod bound is the contract, both limits applied to
 * the TRIMMED value since `.trim()` transforms before the checks run). Only
 * meaningful on a MASS goods-receipt line (a count line has no moisture basis
 * at all — see massGoodsReceiptLineInputSchema vs
 * countGoodsReceiptLineInputSchema below). Supplying this on an IN-BAND
 * reading is not a schema-level error — it simply has no effect at POST time
 * (the override only engages when the reading is actually out-of-band; see
 * goods-receipts.ts's POST handler and its test "(d)"). */
const mcOverrideReasonSchema = z
    .string()
    .trim()
    .min(3, 'mcOverrideReason must be at least 3 characters')
    .max(500, 'mcOverrideReason must be at most 500 characters');
// ---------------------------------------------------------------------------
// Weighbridge tickets (§5.4, §8.1 step 2, §8.4 step 2)
// ---------------------------------------------------------------------------
const ticketBaseSchema = z.object({
    siteId: ulidSchema,
    direction: z.enum(TICKET_DIRECTIONS),
    externalTicketNo: z.string().min(1).max(100).nullable().optional(),
    vehicleReg: z.string().min(1).max(32),
    haulier: z.string().max(200).nullable().optional(),
    driver: z.string().max(200).nullable().optional(),
    grossKg: nonNegativeKgSchema,
    tareKg: nonNegativeKgSchema,
    mcPct: moisturePctSchema.nullable().optional(),
    mcSource: z.enum(MC_SOURCES).nullable().optional(),
    weighedAt: z.coerce.date(),
    purchaseOrderId: ulidSchema.nullable().optional(),
    salesOrderId: ulidSchema.nullable().optional(),
    customerId: ulidSchema.nullable().optional(),
    productId: ulidSchema,
    notes: z.string().nullable().optional(),
});
/** Cross-field rule: gross must be >= tare (net_kg = gross - tare can never be
 * negative — mirrors the DB CHECK in schema/intake.ts). A ticket that carries an
 * `mcSource` reading should also carry the `mcPct` it was read from. */
function checkTicket(val, ctx) {
    if (Number(val.tareKg) > Number(val.grossKg)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'tareKg must not exceed grossKg (net_kg cannot be negative)',
            path: ['tareKg'],
        });
    }
    if (val.mcSource != null && val.mcPct == null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'mcPct is required when mcSource is set',
            path: ['mcPct'],
        });
    }
}
export const ticketCreateSchema = ticketBaseSchema.superRefine(checkTicket);
// Edit (PATCH, DRAFT-only) accepts the exact same shape as create — a full
// replace of the header, matching "documents are freely editable in DRAFT"
// (CLAUDE.md conventions; same precedent as schemas/stores.ts/sales.ts).
export const ticketUpdateSchema = ticketCreateSchema;
export const ticketListQuerySchema = listQueryBaseSchema.extend({
    status: z.enum(TICKET_STATUSES).optional(),
    direction: z.enum(TICKET_DIRECTIONS).optional(),
    purchaseOrderId: ulidSchema.optional(),
});
export const ticketResponseSchema = z.object({
    id: ulidSchema,
    tenantId: ulidSchema,
    siteId: ulidSchema,
    number: z.string(),
    direction: z.enum(TICKET_DIRECTIONS),
    externalTicketNo: z.string().nullable(),
    vehicleReg: z.string(),
    haulier: z.string().nullable(),
    driver: z.string().nullable(),
    grossKg: z.string(),
    tareKg: z.string(),
    netKg: z.string(),
    mcPct: z.string().nullable(),
    mcSource: z.enum(MC_SOURCES).nullable(),
    weighedAt: z.coerce.date(),
    recordedAt: z.coerce.date(),
    status: z.enum(TICKET_STATUSES),
    purchaseOrderId: ulidSchema.nullable(),
    salesOrderId: ulidSchema.nullable(),
    customerId: ulidSchema.nullable(),
    productId: ulidSchema,
    notes: z.string().nullable(),
});
// ---------------------------------------------------------------------------
// Goods receipts (+ lines) — §5.4, §7, §8.1 steps 3-4
// ---------------------------------------------------------------------------
/** Receipt against a mass-priced PO line — the original, still-default shape.
 * `.strict()` so a line can never mix this shape's `qtyKg` with the count
 * shape's `qty` (mirrors schemas/purchasing.ts's massLineSchema/countLineSchema
 * split and packages/db/src/schema/intake.ts's `goods_receipt_lines_quantity_mode`
 * CHECK). */
const massGoodsReceiptLineInputSchema = z
    .object({
    poLineId: ulidSchema,
    productId: ulidSchema,
    qtyKg: positiveKgSchema,
    mcPct: moisturePctSchema.nullable().optional(),
    /** MOISTURE_BAND ADMIN override reason (spec §7 amendment R8) — optional at
     * the schema layer (a line may be created before the moisture reading is
     * known to be out-of-band); the API hard-blocks POST when the reading turns
     * out to be out-of-band and this is absent. Only ever present on a mass
     * line — no moisture basis exists for a count line, so it has no analogue
     * on countGoodsReceiptLineInputSchema below. */
    mcOverrideReason: mcOverrideReasonSchema.optional(),
})
    .strict();
/** Receipt against an EACH-priced (count) PO line — no moisture basis, so no
 * `mcPct` (a count line's PO line was never moisture-priced either). `.strict()`
 * — see massGoodsReceiptLineInputSchema's doc comment. */
const countGoodsReceiptLineInputSchema = z
    .object({
    poLineId: ulidSchema,
    productId: ulidSchema,
    qty: positiveEachQtySchema,
})
    .strict();
/** A goods-receipt line is EITHER a mass receipt (`qtyKg`) OR a count receipt
 * (`qty`) — never both, never neither. Same `.strict()`-branches-as-guard
 * technique as schemas/purchasing.ts's purchaseOrderLineInputSchema. */
export const goodsReceiptLineInputSchema = z.union([massGoodsReceiptLineInputSchema, countGoodsReceiptLineInputSchema]);
export const isCountLine = ((line) => line.qty != null);
/** Complement of `isCountLine` — see its doc comment. */
export const isMassLine = ((line) => line.qty == null);
export const goodsReceiptWriteSchema = z.object({
    poId: ulidSchema,
    ticketId: ulidSchema.nullable().optional(),
    locationId: ulidSchema,
    receivedAt: z.coerce.date(),
    notes: z.string().nullable().optional(),
    lines: z.array(goodsReceiptLineInputSchema).min(1, 'at least one line is required'),
});
// Edit (PATCH, DRAFT-only) accepts the exact same shape as create — a full
// replace of header + lines (same precedent as dispatchUpdateSchema/transferUpdateSchema).
export const goodsReceiptUpdateSchema = goodsReceiptWriteSchema;
export const goodsReceiptListQuerySchema = listQueryBaseSchema.extend({
    status: z.enum(GOODS_RECEIPT_STATUSES).optional(),
    poId: ulidSchema.optional(),
});
/** A GR line is either a mass receipt (`qtyKg` populated, `qty` null) or a
 * count receipt (`qty` populated, `qtyKg`/`mcPct` null) — the
 * `goods_receipt_lines_quantity_mode` CHECK (packages/db/src/schema/intake.ts).
 * Both quantity fields are therefore nullable here; discriminate with
 * `isCountLine`/`isMassLine` above. (Widened in the G-A2 gate fix round
 * 2026-07-07 — this schema previously claimed `qtyKg` non-null, which the API
 * stopped honouring the moment EACH receipts landed.) */
export const goodsReceiptLineResponseSchema = z.object({
    id: ulidSchema,
    poLineId: ulidSchema,
    productId: ulidSchema,
    qtyKg: z.string().nullable(),
    qty: z.string().nullable(),
    mcPct: z.string().nullable(),
    // Populated only once the goods receipt has been POSTED (§7) — null on a DRAFT line.
    payableCents: z.string().nullable(),
    vatCents: z.string().nullable(),
    // MOISTURE_BAND ADMIN override (spec §7 amendment R8) — mcOverridden is only
    // ever flipped true at POST time, when the reading was actually out-of-band
    // AND an ADMIN posted with a reason (goods-receipts.ts's POST handler);
    // mcOverrideReason may be populated (from the line input) even when
    // mcOverridden is false — e.g. a reason supplied "just in case" against an
    // in-band reading is stored but ignored (see the DB CHECK's own comment).
    mcOverridden: z.boolean(),
    mcOverrideReason: z.string().nullable(),
});
export const goodsReceiptResponseSchema = z.object({
    id: ulidSchema,
    tenantId: ulidSchema,
    number: z.string(),
    poId: ulidSchema,
    ticketId: ulidSchema.nullable(),
    status: z.enum(GOODS_RECEIPT_STATUSES),
    receivedAt: z.coerce.date(),
    locationId: ulidSchema,
    notes: z.string().nullable(),
    lines: z.array(goodsReceiptLineResponseSchema),
});
/** One `recordMovement` result surfaced back to the caller as part of the posting
 * summary — money transported as decimal strings (JSON has no bigint). Mirrors
 * schemas/stores.ts's storesIssueMovementSummarySchema. `qtyKg`/`qty` follow the
 * same exactly-one-populated mass/count split as goodsReceiptLineResponseSchema. */
export const goodsReceiptMovementSummarySchema = z.object({
    lineId: ulidSchema,
    productId: ulidSchema,
    qtyKg: z.string().nullable(),
    qty: z.string().nullable(),
    payableCents: z.string(),
    vatCents: z.string(),
    movementId: ulidSchema,
});
export const goodsReceiptPostResponseSchema = z.object({
    goodsReceipt: goodsReceiptResponseSchema,
    movements: z.array(goodsReceiptMovementSummarySchema),
    summary: z.string(),
});
export const goodsReceiptReverseResponseSchema = z.object({
    goodsReceipt: goodsReceiptResponseSchema,
    reversedMovementIds: z.array(ulidSchema),
});
// ---------------------------------------------------------------------------
// Landed cost docs — §5.4, §9.3, §8.1 step 5
// ---------------------------------------------------------------------------
export const landedCostDocWriteSchema = z.object({
    supplierId: ulidSchema,
    description: z.string().min(1).max(500),
    amountCents: moneyCentsSchema.refine((v) => v > 0n, 'amountCents must be positive'),
    /** The goods_receipt_lines this cost is allocated across (by value, §9.3). At
     * least one target line is required. */
    grLineIds: z.array(ulidSchema).min(1, 'at least one goods receipt line is required'),
    notes: z.string().nullable().optional(),
});
// Edit (PATCH, DRAFT-only) accepts the exact same shape as create.
export const landedCostDocUpdateSchema = landedCostDocWriteSchema;
export const landedCostDocListQuerySchema = listQueryBaseSchema.extend({
    status: z.enum(LANDED_COST_DOC_STATUSES).optional(),
    supplierId: ulidSchema.optional(),
});
/** Wire shape of one `landed_cost_docs.allocations` entry — `amountCents` is null
 * until POST (see schema/intake.ts's file header). */
export const landedCostAllocationSchema = z.object({
    grLineId: ulidSchema,
    amountCents: z.string().nullable(),
});
export const landedCostDocResponseSchema = z.object({
    id: ulidSchema,
    tenantId: ulidSchema,
    number: z.string(),
    supplierId: ulidSchema,
    description: z.string(),
    amountCents: z.string(),
    status: z.enum(LANDED_COST_DOC_STATUSES),
    allocations: z.array(landedCostAllocationSchema),
    notes: z.string().nullable(),
});
export const landedCostPostResponseSchema = z.object({
    landedCostDoc: landedCostDocResponseSchema,
    summary: z.string(),
});
export const landedCostReverseResponseSchema = z.object({
    landedCostDoc: landedCostDocResponseSchema,
    reversedMovementIds: z.array(ulidSchema),
});
//# sourceMappingURL=intake.js.map